import React, { useEffect, useState } from "react";

import { createPortal } from "react-dom";

import iconBau from "./chrome/icon-chest.webp";
import Confete from "./Confete";
import FalaDoMascote from "./FalaDoMascote";
import { prefereMenosMovimento, useTextoDigitado } from "./textoDigitado";

// Suspense antes da revelação. Sem o tremor visível para justificar a espera,
// 1,5s vira só um atraso sem sentido — por isso encolhe para quem prefere menos
// movimento, em vez de forçar a mesma pausa às cegas.
//
// 1150 + 350 = os mesmos ~1,5s de antes, agora divididos em ANTECIPAÇÃO e IMPACTO.
// Até 03/08/2026 havia só o tremor, em laço, e a carta entrava por corte seco: o baú
// nunca abria, e a sensação era de vídeo cortado no meio. Abrir baú em jogo é
// antecipação -> impacto -> recompensa, e faltava o do meio.
function atrasoDoTremor() {
  return prefereMenosMovimento() ? 50 : 1150;
}

// Duração do estouro: o baú salta e o clarão toma a caixa. A troca de conteúdo
// acontece ESCONDIDA dentro do clarão — é ele que emenda o baú e a carta, e é por
// isso que não há sprite de baú aberto para desenhar (não existe: o único asset é o
// baú fechado). Zero sob movimento reduzido, e aí não há clarão nenhum: um flash
// branco é justamente o que essa preferência pede para evitar.
function duracaoDoEstouro() {
  return prefereMenosMovimento() ? 0 : 350;
}

// Quanto o clarão leva para apagar sobre a carta. Mais longo que o estouro de
// propósito: a luz entra em golpe e sai devagar, que é como o olho espera que luz
// forte se dissipe — e é esse tempo que a carta ganha para "surgir de dentro dela"
// junto com o `tl-item-burst`.
const DURACAO_DO_APAGAR_MS = 450;

// Respiro entre o fim da fala e o convite para prosseguir. Existe para o olho
// fechar a leitura da última palavra: sem ele o texto termina e a instrução
// aparece no MESMO quadro, e os dois eventos disputam a atenção. Sob movimento
// reduzido a fala já nasce completa, então não há nada a esperar.
function atrasoDaInstrucao() {
  return prefereMenosMovimento() ? 0 : 400;
}

// O chamariz que entra junto com o convite. Três classes e não duas desde
// 24/08/2026 — ver o comentário no JSX: os pulsos da carta são anéis retangulares e
// a figura do arco não tem retângulo.
function classeDoPulso(card, semCarta) {
  if (semCarta) return "tl-anim-pulse-figura";
  return card.tipo === "raro"
    ? "tl-anim-pulse-carta-raro"
    : "tl-anim-pulse-carta";
}

// O que o toque faz quando a fala já terminou.
function rotuloDoToque(card, semCarta) {
  return semCarta ? "Continuar" : `Guardar a carta ${card.nome}`;
}

/**
 * Entrega de recompensa: baú fechado → toque → tremor de suspense → revelação
 * da carta, com o mascote comentando.
 *
 * DOIS ASSUNTOS POSSÍVEIS desde 24/08/2026, e um componente só:
 *
 *   `card`  o aluno ganhou a carta. Baú, tremor, estouro, e a carta com moldura.
 *   `arco`  a CENA do fim de leva, quando a carta rara não foi sorteada. Nem baú
 *           nem clarão: entra direto na revelação, com o mascote no estado do arco
 *           em vez da carta, e o toque não guarda nada.
 *
 * Um componente e não dois porque o que muda é pouco e o que fica é muito: o
 * painel, o degradê, os raios que giram, o mascote falando, a fala digitada em dois
 * estágios de toque, o convite no rodapé, o layout em paisagem de 1040x520 a partir
 * de 768px, o par fantasma da bolha. Duplicar tudo isso para trocar a imagem e o
 * rótulo do botão daria duas telas que divergem na primeira correção que uma receber
 * e a outra não.
 *
 * NOME E FALA SAEM DO MESMO LUGAR nos dois casos (`nome`, `pilula`), e é por isso
 * que `arcoDaFaseRara` chama de `pilula` o que seria mais honesto chamar de fala:
 * com os campos iguais, o corpo do componente trata os dois assuntos pelo mesmo
 * caminho, e só a FIGURA e o rótulo do botão têm ramo.
 *
 * @param {object}   [card]   { nome, asset, pilula, tipo, comemorar }
 * @param {object}   [arco]   { nome, figura, pilula, comemorar }
 * @param {object}   mascote  { nome, cabeca }
 * @param {Function} onFechar
 */
function CerimoniaCard({ card, arco, mascote, onFechar }) {
  // O que está sendo apresentado. `card` ganha quando os dois chegam — não deveria
  // acontecer (`cenaDaFase` devolve um ou outro), e se acontecer é melhor entregar a
  // carta do que a cena: a carta é o que o servidor vai gravar.
  const assunto = card || arco || {};
  // SEM CARTA = sem baú. Não há o que abrir, e o baú prometeria um prêmio que não
  // vem — o que seria pior que não ter cerimônia nenhuma.
  const semCarta = !card && Boolean(arco);

  // 'bau' -> 'abrindo' (tremor) -> 'estourando' (salto + clarão) -> 'revelado'.
  // 'estourando' entrou em 03/08/2026 e é o momento em que o baú abre: sem ela o
  // tremor era antecipação sem impacto, e a carta aparecia por corte.
  //
  // A cena do arco começa em 'revelado' e nunca sai de lá: as três primeiras etapas
  // são a abertura de um baú.
  const [etapa, setEtapa] = useState(semCarta ? "revelado" : "bau");
  // Estado PRÓPRIO, e não derivado da etapa: o clarão atravessa a fronteira entre
  // 'estourando' e 'revelado' (é isso que ele existe para esconder) e some sozinho
  // depois. Derivado da etapa, ele ficaria montado sobre a carta para sempre.
  const [clarao, setClarao] = useState(null); // null | 'acendendo' | 'apagando'
  // O confete vem do `comemorar` do ASSUNTO, e a cena de ajuda também comemora
  // (decisão do Kilson em 21/08/2026): é a comemoração do ARCO, não do prêmio. Sem
  // carta o aluno viu a mesma cena e ouviu a mesma fala; comemorar só quando cai
  // prêmio faria a comemoração ser do prêmio.
  const mostrarConfete = etapa === "revelado" && Boolean(assunto.comemorar);

  // A fala só começa a ser digitada quando a carta aparece: passar `""` até lá e a
  // pílula depois é o que dispara o hook na hora certa (ele reinicia quando o texto
  // muda). Sem isso a digitação correria durante os 1,5s de suspense do baú e o
  // texto chegaria pronto.
  const { digitado, digitando, completar } = useTextoDigitado(
    etapa === "revelado" ? assunto.pilula : ""
  );

  // O convite para prosseguir só entra quando prosseguir é o que o toque FAZ. As
  // pílulas têm 92 a 190 caracteres (4,1s a 8,5s de fala), então uma instrução
  // visível desde o primeiro caractere passava 5 segundos convidando a criança a
  // interromper o conteúdo educativo — que é a razão de o card existir.
  const [convitePronto, setConvitePronto] = useState(false);

  useEffect(() => {
    if (etapa !== "revelado" || digitando) {
      setConvitePronto(false);
      return undefined;
    }
    const id = setTimeout(() => setConvitePronto(true), atrasoDaInstrucao());
    return () => clearTimeout(id);
  }, [etapa, digitando]);

  function abrirBau() {
    setEtapa("abrindo");
    setTimeout(() => {
      // Sob movimento reduzido não há estouro nem clarão: pula direto, como antes.
      // Um flash branco é exatamente o que essa preferência pede para evitar.
      if (duracaoDoEstouro() === 0) {
        setEtapa("revelado");
        return;
      }
      setEtapa("estourando");
      setClarao("acendendo");
      setTimeout(() => {
        // A troca cai NO PICO do clarão, não depois dele: é o que faz o corte ficar
        // invisível. Daqui em diante a luz apaga por cima da carta já montada.
        setEtapa("revelado");
        setClarao("apagando");
        setTimeout(() => setClarao(null), DURACAO_DO_APAGAR_MS);
      }, duracaoDoEstouro());
    }, atrasoDoTremor());
  }

  // Toque em dois estágios, como caixa de diálogo de jogo: o primeiro termina a
  // fala, o segundo guarda a carta. Protege o texto de quem tem pressa sem prender
  // ninguém esperando 8,5s — o atalho dá resposta imediata e visível (o texto salta
  // para o fim), então não lê como toque ignorado.
  function tocarNaCaixa() {
    if (digitando) {
      completar();
      return;
    }
    onFechar();
  }

  // Portal para o body, envolto em .trilha-ludica: fora da árvore do módulo os
  // tokens não seriam herdados, e position:fixed dentro de qualquer ancestral
  // com transform passaria a se posicionar contra ele em vez da viewport.
  return createPortal(
    <div className="trilha-ludica">
      <div
        className="trilha-ludica__cerimonia tl-anim-fade-in"
        role="dialog"
        aria-modal="true"
        /* O rótulo diz o que a caixa É, e são três coisas diferentes: a espera
           do baú, a carta revelada e a cena do arco. A cena não é "Card ...":
           não há card, e anunciar um faria o leitor de tela prometer o prêmio que
           a tela justamente não entrega. */
        aria-label={
          semCarta
            ? assunto.nome
            : etapa === "revelado"
            ? `Card ${card.nome}`
            : "Você ganhou um card"
        }
      >
        {mostrarConfete && <Confete />}

        {/* onClick no painel inteiro, e não só na carta: um toque em qualquer
            lugar da caixa guarda a carta, o que perdoa o dedo impreciso de uma
            criança de 6 anos. A carta segue sendo o alvo VISÍVEL (pulsa, recebe
            foco) — ela é um <button> de verdade, mas sem handler próprio: o
            clique dela borbulha até aqui. Com handler nos dois, um clique na
            carta chamaria o handler DUAS vezes.

            Só a partir de 'revelado': durante o baú, um toque solto não pode
            pular a etapa de abrir.

            Sem `role`/`onKeyDown` neste div de propósito: ele é uma área de
            toque REDUNDANTE. O caminho acessível é o <button> da carta, que
            recebe foco e responde a Enter/Espaço — e o clique dele borbulha até
            aqui. Dar papel interativo ao painel criaria um segundo alvo de
            teclado para a mesma ação e um segundo anúncio no leitor de tela. */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div
          className={`trilha-ludica__cerimonia-painel tl-anim-pop-in${
            etapa === "revelado"
              ? " trilha-ludica__cerimonia-painel--tocavel"
              : ""
          }`}
          onClick={etapa === "revelado" ? tocarNaCaixa : undefined}
        >
          {/* Dentro do painel (era irmão dele, em position:fixed): é o painel
              que recorta os raios na borda arredondada agora que a revelação é
              uma caixa e não a tela toda. Precisa vir ANTES do conteúdo no HTML
              — com z-index negativo, a ordem do DOM é o que decide o
              empilhamento entre ele e o degradê de fundo do painel.

              Sem condição de etapa (antes só montava em 'revelado'): os feixes
              giram desde a espera do baú, e é isso que dá à espera a sensação de
              que algo acontece ali dentro. */}
          <div
            className="trilha-ludica__raios tl-anim-rays"
            aria-hidden="true"
          />
          {/* O CLARÃO é a abertura do baú. Não há sprite de baú aberto — o único
              asset é o fechado —, então o que faz o papel da tampa é a luz: ela
              nasce de dentro do baú, toma a caixa no pico (é aí que o conteúdo é
              trocado, escondido) e apaga sobre a carta já montada. O corte continua
              existindo; deixa de ser visível.

              Montado só nas duas etapas em que atua, para não haver um elemento a
              mais sobre a carta durante a leitura da fala. Sob movimento reduzido
              nunca aparece: 'estourando' não acontece, e o clarão apagando exigiria
              um flash que essa preferência pede para evitar. */}
          {clarao && (
            <div
              className={`trilha-ludica__clarao trilha-ludica__clarao--${clarao}`}
              aria-hidden="true"
            />
          )}
          {etapa === "revelado" ? (
            <>
              {/* Cena da revelação: um container próprio, e não os três blocos
               soltos no painel, porque em paisagem (>= 768px) eles deixam de ser
               uma pilha e viram duas colunas — carta à esquerda, texto à direita.
               O painel não pode ser esse grid: na etapa do baú ele abriga outro
               conteúdo, que continua centrado.

               A ORDEM DO DOM é a da pilha (cabeçalho -> carta -> fala) e não a
               visual da paisagem, de propósito. É ela que o leitor de tela
               anuncia e que o celular empilha; quem reordena é o
               `grid-template-areas`, que move a carta para a coluna da esquerda
               sem mexer no documento. Só há um focável aqui (a própria carta),
               então reposicionar não embaralha ordem de tabulação nenhuma. */}
              <div className="trilha-ludica__cerimonia-cena">
                {/* Título e selo num bloco só: são UM item do grid (a coluna de
                  texto tem duas faixas, cabeçalho e fala), e o selo é
                  condicional — soltos, a área do grid mudaria de dono conforme o
                  card fosse raro ou comum. */}
                <div className="trilha-ludica__cerimonia-cabecalho">
                  <h2 className="trilha-ludica__cerimonia-titulo">
                    {assunto.nome}
                  </h2>
                  {/* O selo é o do OBJETO, e a cena não ganha um no lugar. Houve a
                      tentação de escrever ali algo como "O fim de uma jornada" — e
                      seria chrome inventando um momento: quem conta o que está
                      acontecendo é o mascote, na fala, que é o mesmo texto da carta.
                      Um subtítulo a mais só ocuparia a faixa do grid que dá o
                      respiro entre o título e a bolha. */}
                  {!semCarta && card.tipo === "raro" && (
                    <p className="trilha-ludica__cerimonia-subtitulo">
                      Novo card raro descoberto!
                    </p>
                  )}
                </div>
                {/* A carta é a recompensa e lê como protagonista: primeiro item
                  depois do título na pilha, coluna inteira à esquerda na
                  paisagem. A fala do mascote comenta a carta, que é o papel real
                  dela.

                  <button> de verdade, e não a <div> de antes: com o "Continuar"
                  removido, a carta passou a ser o único caminho adiante — se não
                  fosse focável e acionável por teclado, quem navega por teclado
                  ou leitor de tela ficaria sem saída no modal. */}
                {/* O PULSO entra junto com o convite, e não desde o começo. Ele é o
                  chamariz mais forte da tela; aceso durante a fala, convidava a
                  interromper com mais força que qualquer texto — e o texto da
                  instrução tinha acabado de sair de cena por esse motivo.

                  O rótulo acompanha o que o toque FAZ: enquanto digita, ele
                  termina a fala; depois, guarda a carta. Um rótulo fixo mentiria
                  para quem navega por teclado durante 4 a 8 segundos. */}
                {/* O CHAMARIZ DA CENA É OUTRO, e não é preferência: os dois pulsos
                  da carta são `box-shadow: 0 0 0 Npx`, ou seja um anel RETANGULAR
                  crescendo a partir da caixa do botão. Sobre a carta funciona — ela
                  é um retângulo com moldura. Sobre a figura do arco, que tem a borda
                  dissolvida de propósito, o anel desenharia justamente o retângulo
                  que a pluma existe para apagar. `tl-anim-pulse-figura` é uma
                  respiração em `scale`, que segue a forma que estiver ali. */}
                <button
                  type="button"
                  className={`trilha-ludica__carta-botao${
                    convitePronto ? ` ${classeDoPulso(card, semCarta)}` : ""
                  }`}
                  /* O rótulo acompanha o que o toque FAZ, e são três casos: durante a
                     fala ele termina a fala; depois, guarda a carta — ou, sem carta,
                     apenas continua. "Guardar a carta" ali seria promessa falsa para
                     quem navega por teclado ou leitor de tela, que é exatamente quem
                     não pode conferir na imagem. */
                  aria-label={
                    digitando
                      ? "Mostrar o texto completo"
                      : rotuloDoToque(card, semCarta)
                  }
                >
                  {semCarta ? (
                    /* SEM `__carta-moldura`: aquele elemento é o que recorta o canto
                       arredondado e hospeda o brilho holográfico (`::after`), e as
                       duas coisas dizem "item de coleção". O recorte também comeria a
                       pluma da borda. O `__carta-burst` fica: é a entrada em escala,
                       e serve a qualquer figura. */
                    <div className="trilha-ludica__carta-burst">
                      <img
                        className="trilha-ludica__arco"
                        src={arco.figura}
                        alt=""
                      />
                    </div>
                  ) : (
                    <div className="trilha-ludica__carta-moldura">
                      <div className="trilha-ludica__carta-burst">
                        <img
                          className="trilha-ludica__carta"
                          src={card.asset}
                          alt=""
                        />
                      </div>
                    </div>
                  )}
                </button>
                <FalaDoMascote
                  mascote={mascote}
                  texto={assunto.pilula}
                  digitado={digitado}
                  digitando={digitando}
                />
              </div>
              {/* A instrução vive FORA da bolha, e isso é o ponto: dentro dela quem
              dizia "toque na tela" era o mascote, que passava de quem ensina a
              quem explica a mecânica do jogo — e competia com o conteúdo
              educativo pelo mesmo bloco e o mesmo fundo. Aqui ela é o que sempre
              foi: chrome, no rodapé da caixa, irmã da legenda do baú (mesmo lugar,
              mesmo tratamento visual, na etapa equivalente).

              Sempre no DOM, aparecendo por opacidade: montar só quando pronta
              faria o rodapé nascer no fim da fala e empurrar o layout — a mesma
              lição do texto fantasma da bolha.

              `aria-hidden` porque para quem usa leitor de tela isto seria eco: o
              <button> da carta já anuncia o que o toque faz, e o rótulo dele
              acompanha os dois estágios. */}
              <p
                className={`trilha-ludica__cerimonia-instrucao${
                  convitePronto
                    ? " trilha-ludica__cerimonia-instrucao--visivel"
                    : ""
                }`}
                aria-hidden="true"
              >
                Toque na tela para continuar
              </p>
            </>
          ) : (
            <>
              <h2 className="trilha-ludica__cerimonia-titulo">
                Você ganhou um card!
              </h2>
              {/* `disabled` em tudo que não é 'bau', e não só em 'abrindo': durante
                  o estouro o baú também não pode receber um segundo toque. */}
              <button
                type="button"
                className={`trilha-ludica__bau${
                  etapa === "bau" ? " tl-anim-pulse-bau" : ""
                }`}
                onClick={abrirBau}
                disabled={etapa !== "bau"}
                aria-label="Toque para abrir e revelar seu novo card"
              >
                {/* Três estados no mesmo asset (o único que existe é o baú
                    fechado): flutuando, tremendo e estourando. O estouro é o que
                    faz as vezes da tampa abrindo — o baú salta na direção de quem
                    olha e se desfaz na luz, em vez de simplesmente sumir. */}
                <img
                  className={`trilha-ludica__bau-icone${
                    etapa === "abrindo"
                      ? " trilha-ludica__bau-icone--abrindo"
                      : ""
                  }${
                    etapa === "estourando"
                      ? " trilha-ludica__bau-icone--estourando"
                      : ""
                  }`}
                  src={iconBau}
                  alt=""
                />
              </button>
              <p className="trilha-ludica__bau-legenda" aria-live="polite">
                {etapa === "bau" ? "Toque para abrir" : "Abrindo…"}
              </p>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default CerimoniaCard;
